/**
 * <giveaway-profile> – UI layer for <gn-giveaway>
 * Renders states (loading / logged-out / logged-in) and type-specific
 * action cards. Listens to gn:* events on a child <gn-giveaway>.
 */
class GiveawayProfile extends HTMLElement {
  constructor() {
    super();
    this._onReady = this._onReady.bind(this);
    this._onSignup = this._onSignup.bind(this);
    this._onAction = this._onAction.bind(this);
    this._onLogout = this._onLogout.bind(this);
    this._onError = this._onError.bind(this);
  }

  connectedCallback() {
    this.gn = this.querySelector('gn-giveaway');
    if (!this.gn) return;

    this.stateEls = {
      loading: this.querySelector('[data-gn-state="loading"]'),
      loggedOut: this.querySelector('[data-gn-state="logged-out"]'),
      loggedIn: this.querySelector('[data-gn-state="logged-in"]'),
      error: this.querySelector('[data-gn-state="error"]'),
    };

    this.gn.addEventListener('gn:ready', this._onReady);
    this.gn.addEventListener('gn:signup', this._onSignup);
    this.gn.addEventListener('gn:action', this._onAction);
    this.gn.addEventListener('gn:logout', this._onLogout);
    this.gn.addEventListener('gn:error', this._onError);

    this._bindSignupForm();
    this._bindLogout();
  }

  /* ---------- State switching ---------- */
  _setState(name) {
    Object.entries(this.stateEls).forEach(([key, el]) => {
      if (!el) return;
      const match =
        (name === 'logged-in' && key === 'loggedIn') ||
        (name === 'logged-out' && key === 'loggedOut') ||
        key === name;
      el.hidden = !match;
    });
  }

  /* ---------- Event handlers ---------- */
  _onReady(e) {
    if (e.detail.loggedIn) {
      this._renderLoggedIn();
      this._setState('logged-in');
    } else {
      this._setState('logged-out');
    }
  }

  _onSignup() {
    this._renderLoggedIn();
    this._setState('logged-in');
    this._flash(this.dataset.msgSignupSuccess || 'Welcome! You have entered the giveaway.');
  }

  _onAction(e) {
    this._updateEntries(e.detail.entries);
    const btn = this.querySelector(`[data-action-btn="${e.detail.actionId}"]`);
    if (btn) {
      btn.disabled = true;
      const card = btn.closest('.gn-profile__action');
      if (card) card.classList.add('gn-profile__action--done');
      const statusEl = card?.querySelector('.gn-profile__action-status');
      if (statusEl) statusEl.textContent = this.dataset.msgActionDone || 'Completed';
    }
  }

  _onLogout() {
    this._setState('logged-out');
  }

  _onError(e) {
    this._flash(e.detail.message || 'Something went wrong', true);
  }

  /* ---------- Signup form ---------- */
  _bindSignupForm() {
    const form = this.querySelector('[data-gn-signup-form]');
    if (!form) return;
    form.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      const emailEl = form.querySelector('[name="email"]');
      const nameEl = form.querySelector('[name="name"]');
      const btn = form.querySelector('button[type="submit"]');
      const email = emailEl?.value?.trim();
      const name = nameEl?.value?.trim() || 'Guest';
      if (!email) return;
      btn.disabled = true;
      btn.classList.add('is-loading');
      const res = await this.gn.signup(email, name);
      btn.disabled = false;
      btn.classList.remove('is-loading');
      if (!res.success) {
        this._flash(res.error || 'Signup failed', true);
      }
    });
  }

  _bindLogout() {
    const btn = this.querySelector('[data-gn-logout]');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      await this.gn.logout();
    });
  }

  /* ---------- Logged-in render ---------- */
  _renderLoggedIn() {
    this._updateEntries(this.gn.entries);
    this._updateReferral();
    this._updateCountdown();
    this._renderActions();
  }

  _updateEntries(count) {
    const el = this.querySelector('[data-gn-entries]');
    if (el) el.textContent = count;
  }

  _updateReferral() {
    const input = this.querySelector('[data-gn-referral-url]');
    const wrap = this.querySelector('[data-gn-referral-wrap]');
    if (!input || !wrap) return;
    if (this.gn.referralUrl) {
      input.value = this.gn.referralUrl;
      wrap.hidden = false;
    } else {
      wrap.hidden = true;
    }
    const copyBtn = this.querySelector('[data-gn-copy]');
    if (copyBtn && !copyBtn._bound) {
      copyBtn._bound = true;
      copyBtn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(input.value);
          const original = copyBtn.textContent;
          copyBtn.textContent = this.dataset.msgCopied || 'Copied!';
          setTimeout(() => (copyBtn.textContent = original), 1800);
        } catch (_) {}
      });
    }
  }

  _updateCountdown() {
    const el = this.querySelector('[data-gn-end-date]');
    if (!el || !this.gn.endDateMs) return;
    const d = new Date(this.gn.endDateMs);
    if (isNaN(d.getTime())) return;
    el.textContent = d.toLocaleDateString(document.documentElement.lang || 'en', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  /* ---------- Actions ---------- */
  _renderActions() {
    const list = this.querySelector('[data-gn-actions]');
    if (!list) return;
    list.innerHTML = '';
    const actions = this.gn.actions || [];
    if (!actions.length) {
      list.innerHTML = `<li class="gn-profile__actions-empty">${this.dataset.msgNoActions || 'No actions available.'}</li>`;
      return;
    }
    actions.forEach((a) => list.appendChild(this._buildActionCard(a)));
  }

  _buildActionCard(a) {
    const li = document.createElement('li');
    li.className = `gn-profile__action gn-profile__action--${this._typeSlug(a.Type)}${a.done ? ' gn-profile__action--done' : ''}`;

    const entries = a.Entries || a.ent || 1;
    const title = a.Title || a.title || a.Name || this._typeLabel(a.Type);
    const icon = this._iconFor(a.Type);

    li.innerHTML = `
      <div class="gn-profile__action-icon" aria-hidden="true">${icon}</div>
      <div class="gn-profile__action-body">
        <div class="gn-profile__action-head">
          <h3 class="gn-profile__action-title">${this._esc(title)}</h3>
          <span class="gn-profile__action-reward">+${entries} ${entries > 1 ? (this.dataset.msgTickets || 'tickets') : (this.dataset.msgTicket || 'ticket')}</span>
        </div>
        <div class="gn-profile__action-content"></div>
        <div class="gn-profile__action-foot">
          <span class="gn-profile__action-status">${a.done ? (this.dataset.msgActionDone || 'Completed') : ''}</span>
        </div>
      </div>
    `;

    const content = li.querySelector('.gn-profile__action-content');
    const foot = li.querySelector('.gn-profile__action-foot');
    this._renderActionByType(a, content, foot, li);
    return li;
  }

  _renderActionByType(a, content, foot, li) {
    const done = a.done;
    const gn = this.gn;
    const label = this.dataset.msgDo || 'Complete';

    const addButton = (text, handler, opts = {}) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `button button--${opts.style || 'primary'} gn-profile__action-btn`;
      btn.textContent = text;
      btn.dataset.actionBtn = a.Id;
      if (done) btn.disabled = true;
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        btn.classList.add('is-loading');
        if (opts.preOpen) opts.preOpen();
        const res = await gn.doAction(a.Id, opts.value || '');
        btn.classList.remove('is-loading');
        if (!res.success) {
          btn.disabled = false;
          this._flash(res.error || 'Action failed', true);
        }
      });
      foot.insertBefore(btn, foot.firstChild);
    };

    switch (a.Type) {
      case 'refer-friend': {
        const url = gn.referralUrl || '';
        content.innerHTML = `
          <p class="gn-profile__action-desc">${this.dataset.msgReferDesc || 'Share your link. Earn tickets each time a friend joins.'}</p>
          <div class="gn-profile__copy">
            <input type="text" readonly class="field__input" value="${this._esc(url)}" aria-label="Referral link">
            <button type="button" class="button button--secondary" data-gn-copy>${this.dataset.msgCopy || 'Copy'}</button>
          </div>`;
        const input = content.querySelector('input');
        content.querySelector('[data-gn-copy]').addEventListener('click', async () => {
          try {
            await navigator.clipboard.writeText(input.value);
            this._flash(this.dataset.msgCopied || 'Copied!');
          } catch (_) {}
        });
        break;
      }

      case 'visit-page':
      case 'visit-url':
      case 'visit-product':
      case 'visit-collection': {
        const href = a.Validation && a.Validation !== '-' ? a.Validation : '#';
        content.innerHTML = `<p class="gn-profile__action-desc">${this.dataset.msgVisitDesc || 'Visit the page to earn tickets.'}</p>`;
        addButton(this.dataset.msgVisit || 'Visit', null, {
          preOpen: () => window.open(href, '_blank', 'noopener'),
        });
        break;
      }

      case 'facebook-follow':
      case 'instagram-follow':
      case 'twitter-follow':
      case 'tiktok-follow':
      case 'youtube-subscribe':
      case 'pinterest-follow': {
        const platform = a.Type.split('-')[0];
        const href = a.Validation && a.Validation !== '-' ? a.Validation : '#';
        content.innerHTML = `<p class="gn-profile__action-desc">${this.dataset.msgSocialDesc || 'Follow us to earn tickets.'} <span class="gn-profile__platform">${platform}</span></p>`;
        addButton(this.dataset.msgFollow || 'Follow', null, {
          preOpen: () => window.open(href, '_blank', 'noopener'),
        });
        break;
      }

      case 'facebook-share':
      case 'twitter-share': {
        const href = a.Validation && a.Validation !== '-' ? a.Validation : window.location.href;
        content.innerHTML = `<p class="gn-profile__action-desc">${this.dataset.msgShareDesc || 'Share with your network.'}</p>`;
        addButton(this.dataset.msgShare || 'Share', null, {
          preOpen: () => {
            const share =
              a.Type === 'facebook-share'
                ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(href)}`
                : `https://twitter.com/intent/tweet?url=${encodeURIComponent(href)}`;
            window.open(share, '_blank', 'noopener,width=600,height=500');
          },
        });
        break;
      }

      case 'newsletter-signup':
      case 'subscribe': {
        content.innerHTML = `<p class="gn-profile__action-desc">${this.dataset.msgSubscribeDesc || 'Subscribe to our newsletter.'}</p>`;
        addButton(this.dataset.msgSubscribe || 'Subscribe', null);
        break;
      }

      default: {
        content.innerHTML = `<p class="gn-profile__action-desc">${this._esc(a.Description || a.desc || '')}</p>`;
        addButton(label, null);
      }
    }
  }

  /* ---------- Helpers ---------- */
  _iconFor(type) {
    const t = (type || '').toLowerCase();
    if (t.includes('refer')) return this._svgRefer();
    if (t.includes('visit')) return this._svgVisit();
    if (t.includes('facebook')) return this._svgFacebook();
    if (t.includes('instagram')) return this._svgInstagram();
    if (t.includes('twitter')) return this._svgTwitter();
    if (t.includes('tiktok')) return this._svgTikTok();
    if (t.includes('youtube')) return this._svgYouTube();
    if (t.includes('pinterest')) return this._svgPinterest();
    if (t.includes('subscribe') || t.includes('newsletter')) return this._svgMail();
    if (t.includes('share')) return this._svgShare();
    return this._svgTicket();
  }

  _svgTicket() { return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 12V22H4V12M22 7H2V12H22V7ZM12 22V7M12 7H7.5a2.5 2.5 0 110-5c3.5 0 4.5 5 4.5 5zM12 7h4.5a2.5 2.5 0 100-5c-3.5 0-4.5 5-4.5 5z" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
  _svgRefer() { return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H2v-2a4 4 0 015-3.87M12 12a4 4 0 100-8 4 4 0 000 8zm8 0a3 3 0 100-6 3 3 0 000 6zM4 12a3 3 0 100-6 3 3 0 000 6z" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
  _svgVisit() { return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M15 3h6v6M10 14L21 3M18 13v7H4V6h7" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
  _svgShare() { return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
  _svgMail() { return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 4h16v16H4zM4 8l8 5 8-5" stroke-linecap="round" stroke-linejoin="round"/></svg>`; }
  _svgFacebook() { return `<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M13 22v-8h3l.5-4H13V7.5c0-1.1.4-2 2-2h1.5V2H13c-3 0-4.5 1.8-4.5 4.5V10H6v4h2.5v8H13z"/></svg>`; }
  _svgInstagram() { return `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg>`; }
  _svgTwitter() { return `<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M18.2 2H21l-6.5 7.4L22 22h-6.2l-4.8-6.3L5.3 22H2.5l7-8L2 2h6.3l4.4 5.8L18.2 2z"/></svg>`; }
  _svgTikTok() { return `<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M16 2v3a4 4 0 004 4v3a7 7 0 01-4-1.3V16a6 6 0 11-6-6v3a3 3 0 103 3V2h3z"/></svg>`; }
  _svgYouTube() { return `<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M22 8s-.2-1.4-.8-2c-.8-.8-1.6-.8-2-.9C16 5 12 5 12 5s-4 0-7.2.1c-.4.1-1.2.1-2 .9C2.2 6.6 2 8 2 8S1.8 9.7 1.8 11.3v1.4C1.8 14.3 2 16 2 16s.2 1.4.8 2c.8.8 1.8.8 2.3.9C7 19 12 19 12 19s4 0 7.2-.1c.4-.1 1.2-.1 2-.9.6-.6.8-2 .8-2s.2-1.7.2-3.3v-1.4C22.2 9.7 22 8 22 8zM10 14.5v-5l4.5 2.5-4.5 2.5z"/></svg>`; }
  _svgPinterest() { return `<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M12 2a10 10 0 00-3.6 19.3c-.1-.8-.2-2 0-2.9l1.3-5.4s-.3-.7-.3-1.7c0-1.6.9-2.8 2.1-2.8 1 0 1.4.7 1.4 1.6 0 1-.6 2.4-.9 3.8-.3 1.1.6 2.1 1.7 2.1 2 0 3.6-2.2 3.6-5.3 0-2.8-2-4.7-4.8-4.7-3.3 0-5.2 2.5-5.2 5 0 1 .4 2 .9 2.6.1.1.1.2.1.3l-.3 1.2c0 .2-.2.2-.4.1-1.4-.6-2.2-2.7-2.2-4.3 0-3.5 2.5-6.7 7.3-6.7 3.8 0 6.8 2.7 6.8 6.4 0 3.8-2.4 6.9-5.8 6.9-1.1 0-2.2-.6-2.6-1.3l-.7 2.7c-.3 1-1 2.3-1.4 3A10 10 0 1012 2z"/></svg>`; }

  _typeSlug(t) { return (t || 'default').toLowerCase().replace(/[^a-z0-9]+/g, '-'); }
  _typeLabel(t) {
    return (t || '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  _esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  _flash(msg, isError = false) {
    const zone = this.querySelector('[data-gn-toast]');
    if (!zone) return;
    zone.textContent = msg;
    zone.classList.toggle('gn-profile__toast--error', isError);
    zone.classList.add('gn-profile__toast--visible');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => zone.classList.remove('gn-profile__toast--visible'), 3200);
  }
}

customElements.define('giveaway-profile', GiveawayProfile);
